import { fmtUSD, safePnl } from '../../utils/formatters';
import { calcTradeStats } from '../../services/statsCalculator';

/**
 * TagsTab — Event Tag Analysis.
 * Extracted from TradingJournal.js (Phase 3).
 */
const TagsTab = ({
  darkMode,
  filteredTrades,
  closedFilteredTrades,
}) => {
  return (
    <div className={`rounded-lg shadow-md p-6 transition-colors duration-300 ${darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/30 backdrop-blur-md border border-white/30'}`}>
      <h2 className={`text-xl font-bold mb-4 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        Event Tag Analysis
      </h2>
      <p className={`mb-6 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        Analyze your trading performance by market events and conditions. See how news events, FOMC days, and other factors affect your results.
      </p>

      {filteredTrades.length === 0 ? (
        <div className={`text-center py-8 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          No trades found. Add some trades with event tags to see analytics!
        </div>
      ) : (
        <div className="space-y-6">
          {/* Overall Tag Summary */}
          {(() => {
            const tradesWithTags = closedFilteredTrades.filter(t => t.tags && t.tags.length > 0);
            const tradesWithoutTags = closedFilteredTrades.filter(t => !t.tags || t.tags.length === 0);

            const withStats = calcTradeStats(tradesWithTags);
            const withoutStats = calcTradeStats(tradesWithoutTags);
            const pnlWithTags = withStats.totalPnL;
            const pnlWithoutTags = withoutStats.totalPnL;
            const winRateWithTags = withStats.winRate;
            const winRateWithoutTags = withoutStats.winRate;

            // Count unique tags
            const allTags = new Set();
            closedFilteredTrades.forEach(t => {
              if (t.tags) t.tags.forEach(tag => allTags.add(tag));
            });

            return (
              <div className={`p-4 rounded-lg border transition-colors duration-300 ${
                darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/20 backdrop-blur-xs border border-white/20'
              }`}>
                <h3 className={`font-semibold mb-4 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Event Tag Summary
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-600' : 'bg-white'}`}>
                    <div className={`text-xs uppercase tracking-wide mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Tagged Trades
                    </div>
                    <div className="text-lg font-bold text-purple-500">
                      {tradesWithTags.length}
                    </div>
                    <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      ({closedFilteredTrades.length > 0 ? ((tradesWithTags.length / closedFilteredTrades.length) * 100).toFixed(0) : 0}% of trades)
                    </div>
                  </div>
                  <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-600' : 'bg-white'}`}>
                    <div className={`text-xs uppercase tracking-wide mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      P&L on Event Days
                    </div>
                    <div className={`text-lg font-bold ${pnlWithTags >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {pnlWithTags >= 0 ? '' : '-'}${fmtUSD(pnlWithTags)}
                    </div>
                    <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Win Rate: {winRateWithTags.toFixed(0)}%
                    </div>
                  </div>
                  <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-600' : 'bg-white'}`}>
                    <div className={`text-xs uppercase tracking-wide mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      P&L Regular Days
                    </div>
                    <div className={`text-lg font-bold ${pnlWithoutTags >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {pnlWithoutTags >= 0 ? '' : '-'}${fmtUSD(pnlWithoutTags)}
                    </div>
                    <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Win Rate: {winRateWithoutTags.toFixed(0)}%
                    </div>
                  </div>
                  <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-600' : 'bg-white'}`}>
                    <div className={`text-xs uppercase tracking-wide mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Unique Tags
                    </div>
                    <div className="text-lg font-bold text-purple-500">
                      {allTags.size}
                    </div>
                    <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Event types tracked
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Individual Tag Breakdown */}
          <div>
            <h3 className={`font-semibold mb-4 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Performance by Event Tag
            </h3>

            {(() => {
              // Calculate stats for each tag
              const tagStats = {};

              closedFilteredTrades.forEach(trade => {
                if (trade.tags && trade.tags.length > 0) {
                  trade.tags.forEach(tag => {
                    if (!tagStats[tag]) {
                      tagStats[tag] = {
                        trades: [],
                        totalPnL: 0,
                        occurrences: 0
                      };
                    }
                    tagStats[tag].trades.push(trade);
                    tagStats[tag].totalPnL += safePnl(trade);
                    tagStats[tag].occurrences++;
                  });
                }
              });

              // Sort by total P&L (best first)
              const sortedTags = Object.entries(tagStats)
                .map(([name, stats]) => {
                  const avgPnL = stats.occurrences > 0 ? stats.totalPnL / stats.occurrences : 0;
                  const winRate = stats.trades.length > 0
                    ? (stats.trades.filter(t => safePnl(t) > 0).length / stats.trades.length) * 100
                    : 0;
                  const tagTradesWithR = stats.trades.filter(t => t.rMultiple && parseFloat(t.rMultiple) !== 0);
                  const avgR = tagTradesWithR.length > 0
                    ? tagTradesWithR.reduce((sum, t) => sum + parseFloat(t.rMultiple), 0) / tagTradesWithR.length
                    : 0;

                  return {
                    name,
                    occurrences: stats.occurrences,
                    totalPnL: stats.totalPnL,
                    avgPnL,
                    winRate,
                    avgR,
                    trades: stats.trades
                  };
                })
                .sort((a, b) => b.totalPnL - a.totalPnL); // Sort descending (best P&L first)

              if (sortedTags.length === 0) {
                return (
                  <div className={`text-center py-8 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    No event tags logged yet. Tag your trades with market events to identify patterns!
                  </div>
                );
              }

              return (
                <div className="space-y-3">
                  {sortedTags.map((tag, index) => (
                    <div
                      key={tag.name}
                      className={`p-4 rounded-lg border transition-colors duration-300 ${
                        darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/20 backdrop-blur-xs border border-white/20'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className={`text-lg font-bold px-2 py-1 rounded ${
                            tag.totalPnL > 0 ? 'bg-green-500/20 text-green-500' :
                            tag.totalPnL < 0 ? 'bg-red-500/20 text-red-500' :
                            darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                          }`}>
                            #{index + 1}
                          </span>
                          <div>
                            <h4 className={`font-semibold transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {tag.name}
                            </h4>
                            <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {tag.occurrences} trade{tag.occurrences !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-xl font-bold ${tag.totalPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {tag.totalPnL >= 0 ? '+' : '-'}${fmtUSD(tag.totalPnL)}
                          </div>
                          <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Total P&L
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <div className={`text-xs uppercase tracking-wide mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Avg P&L/Trade
                          </div>
                          <div className={`font-semibold ${tag.avgPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {tag.avgPnL >= 0 ? '+' : '-'}${fmtUSD(tag.avgPnL)}
                          </div>
                        </div>
                        <div>
                          <div className={`text-xs uppercase tracking-wide mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Win Rate
                          </div>
                          <div className={`font-semibold ${tag.winRate >= 50 ? 'text-green-500' : 'text-red-500'}`}>
                            {tag.winRate.toFixed(0)}%
                          </div>
                        </div>
                        <div>
                          <div className={`text-xs uppercase tracking-wide mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Avg R
                          </div>
                          <div className={`font-semibold ${tag.avgR >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {tag.avgR >= 0 ? '+' : ''}{tag.avgR.toFixed(2)}R
                          </div>
                        </div>
                        <div>
                          <div className={`text-xs uppercase tracking-wide mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            % of All Trades
                          </div>
                          <div className={`font-semibold transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {closedFilteredTrades.length > 0 ? ((tag.occurrences / closedFilteredTrades.length) * 100).toFixed(0) : 0}%
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default TagsTab;
